import React, { useRef, useEffect, useState } from 'react';

/**
 * BlurhashPlaceholder - Renders a blurhash as a canvas placeholder
 * Based on the working implementation from blurhash_viewer.html
 * 
 * @param {string} hash - The blurhash string to decode and render
 * @param {number} width - Canvas width (default: 32, will be scaled via CSS)
 * @param {number} height - Canvas height (default: 32, will be scaled via CSS)
 */
const BlurhashPlaceholder = ({ hash, width = 32, height = 32, style = {}, ...otherProps }) => {
  const canvasRef = useRef(null);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    // Validate hash
    if (!hash || typeof hash !== 'string' || hash.trim().length === 0) {
      return;
    }

    const canvas = canvasRef.current;
    if (!canvas) return;

    // Use dynamic import to load blurhash
    const renderBlurhash = async () => {
      try {
        const { decode } = await import('blurhash');
        
        // Decode the blurhash to raw pixels (Uint8ClampedArray)
        const pixels = decode(hash, width, height);

        // Set canvas dimensions
        canvas.width = width;
        canvas.height = height;

        // Create ImageData and draw on canvas
        const ctx = canvas.getContext('2d');
        const imageData = ctx.createImageData(width, height);
        imageData.data.set(pixels);
        ctx.putImageData(imageData, 0, 0);

      } catch (error) {
        console.warn('[BlurhashPlaceholder] Failed to decode blurhash:', error);
        setHasError(true);
      }
    };

    renderBlurhash();
  }, [hash, width, height]);

  // Don't render if no valid hash or error
  if (!hash || typeof hash !== 'string' || hash.trim().length === 0 || hasError) {
    return null;
  }

  return (
    <canvas
      ref={canvasRef}
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        ...style,
      }}
      {...otherProps}
    />
  );
};

export default BlurhashPlaceholder;
